import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_RESENDCONFIRM_COPY } from '@/copy/registry'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/** `a-resendconfirm` — the recovery path when the first code never arrived, or a platform
 * refusal (`ACCOUNT_UNCONFIRMED` at sign-in). Non-enumerating (family rule A3). */
export function AResendconfirm({
  email: initialEmail,
  onSent,
  onBackToSignIn,
}: {
  email?: string
  onSent: (email: string) => void
  onBackToSignIn: () => void
}) {
  const [email, setEmail] = useState(initialEmail ?? '')

  const resend = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/confirm/resend', { body: { email } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => onSent(email),
  })

  const problem = resend.error instanceof ApiProblemError ? resend.error.problem : undefined

  return (
    <AuthCard unitId={unitId('a-resendconfirm')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_RESENDCONFIRM_COPY.heading}</h3>
      <AuthCardLead>{A_RESENDCONFIRM_COPY.lead}</AuthCardLead>

      <FieldStack>
        <Field label={A_RESENDCONFIRM_COPY.emailLabel}>
          <input type="email" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </FieldStack>

      {problem && <AResendconfirmRefusal problem={problem} />}

      <Button data-act={actionId('session.resendConfirmation')} disabled={resend.isPending || !email} onClick={() => resend.mutate()}>
        {resend.isPending ? 'Sending…' : A_RESENDCONFIRM_COPY.submitButton}
      </Button>

      <Note>{A_RESENDCONFIRM_COPY.nonEnumeratingNote}</Note>
      <p className="mt-14">
        <a href="#" data-act={actionId('nav.a-signin')} onClick={(e) => { e.preventDefault(); onBackToSignIn() }} className="text-link text-small">
          {A_RESENDCONFIRM_COPY.backToSignInLabel}
        </a>
      </p>
    </AuthCard>
  )
}

function AResendconfirmRefusal({ problem }: { problem: Problem }) {
  if (problem.refusal_kind === 'VALIDATION_FAILED') {
    return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
  }
  if (problem.refusal_kind === 'RATE_LIMITED') return <RateLimitedNotice problem={problem} />
  return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
}
